/**
 * Trainers Report API — multi-session analytics list for the report page.
 */

import { authService } from "../../../utils/authService";

export type TrainersReportFilters = {
  track_id?: string;
  course_id?: string;
  batch_id?: string;
  branch?: string;
  date?: string;
  trainer_id?: string;
  session_category?: string;
  status?: string;
};

export type TrainersReportRow = {
  session_id: string;
  session_name: string;
  student_count: number | null;
  planned_minutes: number | null;
  actual_minutes: number | null;
  trainer_id?: string | null;
  trainer_name: string | null;
  trainer_count: number | null;
  attendance_percent: number | null;
  distraction_percent: number | null;
  avg_quiz_score: number | null;
  grasp_understood_percent: number | null;
  grasp_partial_percent: number | null;
  grasp_did_not_percent: number | null;
  avg_rating: number | null;
  batch_name?: string | null;
  branch?: string | null;
  session_status?: string | null;
  live_session_category?: string | null;
  session_date?: string | null;
};

export type TrainersReportResponse = {
  success: boolean;
  sessions: TrainersReportRow[];
  total: number;
};

function asNum(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function asRow(raw: Record<string, unknown>): TrainersReportRow {
  return {
    session_id: String(raw.session_id ?? ""),
    session_name: String(raw.session_name ?? raw.session_title ?? ""),
    student_count: asNum(raw.student_count ?? raw.no_of_students),
    planned_minutes: asNum(raw.planned_minutes),
    actual_minutes: asNum(raw.actual_minutes),
    trainer_id: raw.trainer_id != null ? String(raw.trainer_id) : null,
    trainer_name:
      raw.trainer_name != null ? String(raw.trainer_name) : null,
    trainer_count: asNum(raw.trainer_count),
    attendance_percent: asNum(raw.attendance_percent),
    distraction_percent: asNum(raw.distraction_percent),
    avg_quiz_score: asNum(raw.avg_quiz_score),
    grasp_understood_percent: asNum(raw.grasp_understood_percent),
    grasp_partial_percent: asNum(raw.grasp_partial_percent),
    grasp_did_not_percent: asNum(raw.grasp_did_not_percent),
    avg_rating: asNum(raw.avg_rating),
    batch_name: raw.batch_name != null ? String(raw.batch_name) : null,
    branch: raw.branch != null ? String(raw.branch) : null,
    session_status:
      raw.session_status != null ? String(raw.session_status) : null,
    live_session_category:
      raw.live_session_category != null
        ? String(raw.live_session_category)
        : null,
    session_date: raw.session_date != null ? String(raw.session_date) : null,
  };
}

export async function fetchTrainersReport(
  filters: TrainersReportFilters,
): Promise<TrainersReportResponse> {
  const res = await authService.post("/session/trainers-report/", filters);
  const data = res?.data || {};
  const list = Array.isArray(data.sessions) ? data.sessions : [];
  return {
    success: Boolean(data.success),
    sessions: list.map((row: Record<string, unknown>) => asRow(row)),
    total: Number(data.total ?? list.length) || 0,
  };
}
